import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { getAllUsers } from 'selectors/entities/userSelectors';
import { getFiles } from 'selectors/entities/fileSelector';

// Returns all articles from state
export const getArticles = (state) => state.getIn(['entities', 'article', 'articles']);

// Return article by id
export const getArticleById = (state, props = {}) => state.getIn(['entities', 'article', 'articles', props.articleId]);

export const getArticleCategoriesFromState = (state) => state.getIn(['entities', 'article', 'articleCategories']);
// Returns all article categories from state
export const getArticleCategories = (state, props = {}) =>
  state.getIn(['entities', 'article', 'articleCategories']).filter((category) => {
    const search = props.search;
    if (!search) {
      return true;
    } else {
      const value = category.get('name') + category.get('text');
      return value?.toLowerCase().indexOf(search.toLowerCase()) !== -1;
    }
  });

// Return category by id
export const getArticleCategoryById = (state, props = {}) =>
  state.getIn(['entities', 'article', 'articleCategories', props.categoryId]);

export const getArticleTagsFromState = (state) => state.getIn(['entities', 'article', 'articleTags']);

// Returns all article tags from state
export const getArticleTags = (state, props = {}) =>
  state
    .getIn(['entities', 'article', 'articleTags'])
    .filter((tag) => {
      const search = props.search;
      if (!search) {
        return true;
      } else {
        return (
          tag
            ?.get('name')
            ?.toLowerCase()
            ?.indexOf(search.toString()) !== -1
        );
      }
    })
    .sort((a, b) => (a.get('timeCreated') > b.get('timeCreated') ? 1 : -1));

// Return tag by id
export const getArticleTagById = (state, props = {}) =>
  state.getIn(['entities', 'article', 'articleTags', props.tagId]);

export const getArticleCategoriesPagination = (state) =>
  state.getIn(['entities', 'article', 'articleCategoriesPagination']);
// Return categories with five articles
export const getLastArticlesCategories = createSelector(
  [getArticles, getArticleCategories, getArticleCategoriesPagination],
  (articles, categories, pagination) => {
    return categories.map((category) => {
      let categoryArticles = articles.filter((article) => article.get('categoryId') === category.get('categoryId'));
      let slice = 5;
      if (pagination.getIn([category.get('categoryId'), 'currentPage'])) {
        slice = pagination.getIn([category.get('categoryId'), 'currentPage']) * 5;
      }
      if (categoryArticles.size !== 0) {
        categoryArticles = categoryArticles
          .toList()
          .sort((a, b) => (a.get('timeCreated') < b.get('timeCreated') ? 1 : -1))
          .slice(0, slice);
      }
      return category.set('lastArticles', categoryArticles);
    });
  },
);

// Return last articles
export const getLastArticles = (state, props = {}) =>
  state
    .getIn(['entities', 'article', 'articles'])
    .filter((article) => article.get('status') === 'published')
    .toList()
    .sort((a, b) => {
      if (props.sortField) {
        return a.get(props.sortField) < b.get(props.sortField) ? 1 : -1;
      }

      return a.get('timeCreated') < b.get('timeCreated') ? 1 : -1;
    })
    .slice(0, props.count ? props.count : 6);

// Return last articles with users
export const getLastArticlesWithUsers = createSelector(
  [getLastArticles, getAllUsers, getArticleCategories],
  (articles, users, categories) => {
    return articles.map((article) => {
      let user = null;

      if (users && article.get('userId')) {
        user = users.get(article.get('userId'));
      }

      return article.set('user', user).set('category', categories.get(article.get('categoryId')));
    });
  },
);

// Return tree categories
export const getTreeCategories = (state) => {
  let categories = state.getIn(['entities', 'article', 'articleCategories']);
  let tree;

  tree = categories.filter((category) => !category.get('parentId'));

  const req = (parent) => {
    if (parent) {
      const children = categories.filter((category) => category.get('parentId') === parent.get('categoryId'));
      return parent.set(
        'categories',
        children.map((child) => req(child)),
      );
    }
  };

  return tree.map((parent) => req(parent));
};

// Returns article with additional related info
export const getArticleWithSummary = createSelector(
  [getArticleById, getArticleCategories, getArticleTags, getAllUsers, getFiles],
  (article, categories, tags, users, allFiles) => {
    if (article) {
      let articleTags,
        articleCategory,
        user,
        files = [];

      if (tags && article.get('tags')) {
        articleTags = article.get('tags').map((id) => tags.get(id));
      }
      if (categories && article.get('categoryId')) {
        articleCategory = categories.get(article.get('categoryId'));
      }
      if (users && article.get('userId')) {
        user = users.get(article.get('userId'));
      }
      if (allFiles && article.get('files')?.size) {
        files = article
          .get('files')
          .map((fileId) => allFiles.get(fileId))
          .filter((file) => file);
      }
      return article.merge(
        fromJS({
          rawTags: article.get('tags'),
          tags: articleTags,
          category: articleCategory,
          user,
          files,
        }),
      );
    }
    return null;
  },
);
